/**
 * Events on this device (2026-10-24, the owner: "I need the card information to stay local to the
 * device, where people can save the event as they do to their card").
 *
 * An event's details - its name, kind, date and time, address, RSVP contact and note - and its
 * guests' names are kept here, in this account's own database (db.ts, "events"), and in the card
 * files of the cards that invite to it (card-file.ts), so they go wherever those go: another
 * device, the card folder, a backup. The server keeps only each guest's code, the card they are
 * on and their answer (apps/api/src/events.ts); a guest's code carries their name and the event's
 * name after a "#" (invite.tsx), which browsers never send to a server.
 */
import type { EventKind } from '../api.ts';
import { req, tx } from './db.ts';
import { cardChanged } from './store.ts';

/** What the host tells guests about the event. */
export interface EventDetails {
  title: string; kind: EventKind; startsOn: string; startsAt: string | null; timeZone: string | null;
  place: string; contact: string; note: string;
}
export interface LocalEvent extends EventDetails {
  id: string;
  /** Each guest's name, by their id on the server. */
  names: Record<string, string>;
  /** The cards that invite to it: when it changes, their files are saved again (the card folder). */
  cardIds: string[];
  updatedAt: string;
}

const listeners = new Set<() => void>();
/** Told when an event on this device changes (the events board, the editor). */
export const onLocalEvents = (f: () => void) => { listeners.add(f); return () => { listeners.delete(f); }; };
const changed = () => { for (const f of listeners) f(); };

export const getLocalEvent = (id: string) =>
  tx(['events'], 'readonly', (t) => req(t.objectStore('events').get(id) as IDBRequest<LocalEvent | undefined>));
export const allLocalEvents = () =>
  tx(['events'], 'readonly', (t) => req(t.objectStore('events').getAll() as IDBRequest<LocalEvent[]>));

/** Keep an event here, as it is now; its cards are saved again with it. */
export async function putLocalEvent(ev: Omit<LocalEvent, 'updatedAt'> & { updatedAt?: string }): Promise<LocalEvent> {
  const row: LocalEvent = { ...ev, cardIds: [...new Set(ev.cardIds)], updatedAt: new Date().toISOString() };
  await tx(['events'], 'readwrite', async (t) => { await req(t.objectStore('events').put(row)); });
  changed();
  for (const id of row.cardIds) await cardChanged(id).catch(() => {});
  return row;
}

/** Change an event that is here (nothing happens to one that is not). */
export async function updateLocalEvent(id: string, change: (ev: LocalEvent) => LocalEvent): Promise<LocalEvent | null> {
  const ev = await getLocalEvent(id);
  return ev ? putLocalEvent(change(ev)) : null;
}

export async function deleteLocalEvent(id: string) {
  await tx(['events'], 'readwrite', async (t) => { await req(t.objectStore('events').delete(id)); });
  changed();
}

/**
 * An event from a card file: added, or merged with the one here - the newer details win, and the
 * guests' names of both are kept (each card file may carry only its own card's guests).
 */
export async function takeLocalEvent(ev: LocalEvent): Promise<void> {
  await tx(['events'], 'readwrite', async (t) => {
    const mine = await req(t.objectStore('events').get(ev.id) as IDBRequest<LocalEvent | undefined>);
    const newer = !mine || ev.updatedAt > mine.updatedAt ? ev : mine;
    const older = newer === ev ? mine : ev;
    const row: LocalEvent = {
      ...newer,
      names: { ...(older?.names ?? {}), ...newer.names },
      cardIds: [...new Set([...(mine?.cardIds ?? []), ...ev.cardIds])],
    };
    await req(t.objectStore('events').put(row));
  });
  changed();
}

const KINDS = ['birthday', 'wedding', 'baby-shower', 'graduation', 'party', 'dinner', 'holiday', 'other'];
const str = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : '');

/** An event as a card file carries it, checked - or null when it is not one. */
export function readLocalEvent(v: unknown): LocalEvent | null {
  const e = v as Record<string, unknown> | null;
  if (!e || typeof e !== 'object' || typeof e.id !== 'string' || !e.id || e.id.length > 64) return null;
  if (typeof e.startsOn !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(e.startsOn)) return null;
  const names: Record<string, string> = {};
  if (e.names && typeof e.names === 'object') {
    for (const [k, n] of Object.entries(e.names as Record<string, unknown>)) if (typeof n === 'string' && k.length <= 64) names[k] = n.slice(0, 80);
  }
  return {
    id: e.id, title: str(e.title, 120) || 'Event', kind: (KINDS.includes(e.kind as string) ? e.kind : 'other') as EventKind,
    startsOn: e.startsOn, startsAt: typeof e.startsAt === 'string' && /^\d{2}:\d{2}$/.test(e.startsAt) ? e.startsAt : null,
    timeZone: str(e.timeZone, 64) || null, place: str(e.place, 300), contact: str(e.contact, 200), note: str(e.note, 500),
    names, cardIds: Array.isArray(e.cardIds) ? e.cardIds.filter((x): x is string => typeof x === 'string').slice(0, 50) : [],
    updatedAt: typeof e.updatedAt === 'string' ? e.updatedAt : new Date(0).toISOString(),
  };
}

/** Until when the server keeps an event's codes and answers: 90 days after its date. */
export function keepUntilOf(startsOn: string): string {
  const [y, m, d] = startsOn.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 90)).toISOString().slice(0, 10);
}
